/**
 * Agent Escalation Queue — boundary contract schemas.
 *
 * Covers the manager-facing agent escalation queue and the per-case
 * agent-suspension endpoints.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/agent-escalation-queue.routes.ts`
 * registers these schemas, and `apps/landlord`'s AgentEscalationQueuePage / lead-detail
 * OverviewTab derive their response types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers:
 *   GET  /landlord/api/agent-escalations
 *   POST /landlord/api/agent-escalations/:id/answer
 *   POST /landlord/api/cases/:id/agent-suspension/resume
 *   GET  /landlord/api/cases/:id/agent-suspension
 *   GET  /landlord/api/cases/:id/agent-escalation
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Quality-review block (BR-QR-ESCALATE) — a reviewer block recorded on an escalation
// ---------------------------------------------------------------------------
/** Tier of the quality-catalog / playbook item a reviewer critique cites. */
export const qualityReviewTierSchema = z.enum(['locked', 'rule', 'default', 'internal']);
export const qualityReviewBlockSchema = z
    .object({
    blockedTool: z.string().describe('MCP tool the reviewer blocked'),
    blockedArgsSummary: z.string().describe('Summary of the blocked call arguments (PII)'),
    critique: z.string().describe('Reviewer critique (PII)'),
    itemId: z.string().describe('Quality-catalog / playbook item id the critique cites'),
    tier: qualityReviewTierSchema,
    itemText: z.string().describe('Text of the cited item (PII)'),
    at: z.iso.datetime().describe('ISO 8601 timestamp of the block'),
})
    .describe('A reviewer block recorded on an escalation');
// ---------------------------------------------------------------------------
// Escalation queue item shape returned in GET response
// ---------------------------------------------------------------------------
export const EscalationListItemSchema = z
    .object({
    id: z.uuid().describe('CaseEscalation ID'),
    caseId: z.uuid().describe('Associated case ID'),
    leadId: z
        .uuid()
        .nullable()
        .describe('Originating leasing Lead ID — the lead itself for a lead case, or the lead a reference check was requested on for a reference-check case; null for any other (e.g. repair) case'),
    repairRequestId: z
        .uuid()
        .nullable()
        .describe('Associated repair ticket ID (Case.id === RepairRequest.id), or null for a non-repair case'),
    agentKindSlug: z.string().describe('Slug of the AgentKind that raised this escalation'),
    question: z.string().describe('Agent-authored escalation question (PII)'),
    createdAt: z.string().describe('ISO 8601 creation timestamp — the queue\'s aging order'),
    qualityReviewBlocks: z
        .array(qualityReviewBlockSchema)
        .describe('Reviewer blocks that raised/extended this escalation, oldest first (BR-QR-ESCALATE)'),
})
    .describe('A single open escalation queue item');
// ---------------------------------------------------------------------------
// Escalation queue list response
// ---------------------------------------------------------------------------
export const EscalationListResponseSchema = z
    .object({
    escalations: z.array(EscalationListItemSchema).describe('Open escalations across the caller\'s authorized cases, oldest first'),
})
    .describe('Agent escalation queue list response');
// ---------------------------------------------------------------------------
// Resolve (answer/dismiss) endpoint path param
// ---------------------------------------------------------------------------
export const EscalationIdParamsSchema = z
    .object({
    id: z.uuid().describe('CaseEscalation ID to resolve'),
})
    .describe('Escalation ID path parameter');
// ---------------------------------------------------------------------------
// Answer body — answer is required
// ---------------------------------------------------------------------------
export const AnswerBodySchema = z
    .object({
    answer: z.string().min(1, 'Answer must not be empty').describe('Landlord-authored answer text'),
})
    .describe('Answer request body');
// ---------------------------------------------------------------------------
// Resolve response — echoes the resolution status
// ---------------------------------------------------------------------------
export const EscalationResolveResponseSchema = z
    .object({
    id: z.uuid().describe('CaseEscalation ID'),
    // Dismiss as a landlord-facing action is removed (BR-ESC-RESOLVE) — answer is the
    // only resolution path this route exposes. But BR-CASE-CLOSE-CLEANUP can still race
    // an in-flight answer() and close the escalation out as 'dismissed' out from under
    // the caller (BR-ESC-ONCE) — the response must reflect that REAL persisted outcome,
    // not assume the caller's call was the one that won.
    status: z.enum(['answered', 'dismissed']).describe('Resolution status actually persisted for the escalation'),
    resolvedAt: z.string().describe('ISO 8601 resolution timestamp'),
})
    .describe('Agent escalation resolution response');
// ---------------------------------------------------------------------------
// Case-scoped path param (agent-suspension resume/read)
// ---------------------------------------------------------------------------
export const CaseIdParamsSchema = z
    .object({
    id: z.uuid().describe('Case ID'),
})
    .describe('Case ID path parameter');
// ---------------------------------------------------------------------------
// Suspension resume response
// ---------------------------------------------------------------------------
export const SuspensionResumeResponseSchema = z
    .object({
    caseId: z.uuid().describe('Case ID'),
    suspended: z.literal(false).describe('Always false — the resume call always ends the active suspension, if any'),
})
    .describe('Agent suspension resume response');
// ---------------------------------------------------------------------------
// Suspension state response (GET agent-suspension)
// ---------------------------------------------------------------------------
export const SuspensionStateResponseSchema = z
    .object({
    caseId: z.uuid().describe('Case ID'),
    suspended: z.boolean().describe('Whether the case currently has an ACTIVE, non-expired agent suspension'),
    causes: z
        .array(z.enum(['escalation_open', 'landlord_takeover', 'manual_pause']))
        .describe('Cause(s) holding the pause open (BR-AGENT-ESC-2, BR-SUSPEND-COMPOSE). Empty when `suspended` is false.'),
    createdAt: z
        .string()
        .nullable()
        .describe('ISO 8601 timestamp the active suspension started. Null when `suspended` is false.'),
    expiresAt: z
        .string()
        .nullable()
        .describe('ISO 8601 timestamp the active suspension auto-expires. Null when `suspended` is false.'),
})
    .describe('Agent suspension state response');
// ---------------------------------------------------------------------------
// Per-case open-escalation read (RA-AGENT-ESC-5)
// ---------------------------------------------------------------------------
export const CaseOpenEscalationResponseSchema = z
    .object({
    escalation: EscalationListItemSchema.nullable().describe('The case\'s open escalation, or null if none'),
})
    .describe('Per-case open agent escalation response');
//# sourceMappingURL=escalation-queue.js.map